import { ChosenTabItem } from '../../components';
import { UserRole } from '../../types/userTypes';
import { CompanyInfo } from './CompanyInfo/CompanyInfo';
import { Integrations } from './Integrations/Integrations';
import { UsersPermissions } from './UsersPermissions/UsersPermissions';
import { AccountPermissions, AccountPermissionsActionsList } from '../../types/generalTypes';
import { DocumentSettings } from './DocumentSettings/DocumentSettings';
import { PersonalInfo } from './PersonalInfo/PersonalInfo';
import { Dunning } from './Dunning/Dunning';

export enum Sections {
	ACCOUNT,
	COMPANY,
	BILLING,
}

export enum AccountTabs {
	PERSONAL_INFO,
}

export enum CompanyTabs {
	COMPANY_INFO,
	USERS_PERMISSIONS,
	INTEGRATIONS,
}

export enum BillingTabs {
	DOCUMENT_SETTINGS,
	DUNNING,
}

type ChosenTabItemComponent = ChosenTabItem & { component: (prop?: any) => React.ReactNode };

export const settingsPageAccountTabs: ChosenTabItemComponent[] = [
	{ label: 'Personal Info', iconImgType: 'user', value: '', component: (prop?: any) => <PersonalInfo /> },
	// { label: 'Password', iconImgType: 'customersEmpty', value: '', component: (prop?: any) => <div>Password</div> },
	// { label: 'Language & Region', iconImgType: 'customersEmpty', value: '', component: (prop?: any) => <div>Language & Region</div> },
];

export const settingsPageCompanyTabs: ChosenTabItemComponent[] = [
	{ label: 'Company Info', value: '', iconImgType: 'office_building', component: (prop?: any) => <CompanyInfo {...prop} /> },
	{ label: 'Users Permissions', value: '', iconImgType: 'users', component: (prop?: any) => <UsersPermissions {...prop} /> },
	{ label: 'Integrations', value: '', iconImgType: 'integrations', component: (prop?: any) => <Integrations {...prop} /> },
];

export const settingsPageBillingTabs: ChosenTabItemComponent[] = [
	{
		label: 'Documents',
		value: '',
		iconImgType: 'writingBoard',
		actionType: AccountPermissionsActionsList.DOCUMENT_SETTINGS,
		component: (prop?: any) => <DocumentSettings {...prop} />,
	},
	{
		label: 'Dunning',
		value: '',
		iconImgType: 'v_circle',
		actionType: AccountPermissionsActionsList.DUNNING,
		component: () => <Dunning />,
	},
];

// Slug maps for deep-linking into a section/tab via query params, e.g.
// `/settings?section=company&tab=company-info`. Kept in sync with the enums above.
const sectionSlugs: Record<string, Sections> = {
	account: Sections.ACCOUNT,
	company: Sections.COMPANY,
	billing: Sections.BILLING,
};

const tabSlugs: Record<Sections, Record<string, number>> = {
	[Sections.ACCOUNT]: { 'personal-info': AccountTabs.PERSONAL_INFO },
	[Sections.COMPANY]: {
		'company-info': CompanyTabs.COMPANY_INFO,
		'users-permissions': CompanyTabs.USERS_PERMISSIONS,
		integrations: CompanyTabs.INTEGRATIONS,
	},
	[Sections.BILLING]: {
		documents: BillingTabs.DOCUMENT_SETTINGS,
		dunning: BillingTabs.DUNNING,
	},
};

export interface ActiveTab {
	activeSection: Sections;
	activeButton: number;
}

// Resolves `?section=…&tab=…` query params to a section/tab selection. Returns
// undefined when no (valid) section is given so callers can fall back to
// router state / the default tab. An unknown/missing tab defaults to the
// section's first tab.
export const resolveActiveTabFromParams = (section?: string | null, tab?: string | null): ActiveTab | undefined => {
	if (!section) {
		return undefined;
	}
	const activeSection = sectionSlugs[section.toLowerCase()];
	if (activeSection === undefined) {
		return undefined;
	}
	const tabsForSection = tabSlugs[activeSection];
	const fromTab = tab ? tabsForSection[tab.toLowerCase()] : undefined;
	return { activeSection, activeButton: fromTab ?? 0 };
};

export interface AuthorizedPage {
	// Stable section identity, used to look up the active section regardless of role-based filtering
	// shifting array positions (see Settings.tsx).
	section: Sections;
	header: string;
	description: string;
	requiredRole?: UserRole;
	items: ChosenTabItemComponent[];
	actionType?: AccountPermissions;
}

export const settingsNestedPages: AuthorizedPage[] = [
	{
		section: Sections.ACCOUNT,
		header: 'settingsAccount',
		description: 'settingsAccountDescription',
		items: settingsPageAccountTabs,
	},
	{
		section: Sections.COMPANY,
		header: 'settingsCompany',
		description: 'settingsCompanyDescription',
		requiredRole: UserRole.Admin,
		items: settingsPageCompanyTabs,
	},
	{
		section: Sections.BILLING,
		header: 'settingsBilling',
		description: '',
		items: settingsPageBillingTabs,
	},
];
